import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {format} from 'date-fns';

import {
  getIsMeetingsCalendarActive,
  getIsUserCalendarConsentInvalid,
  getMeetingsOutsideOfWorkdays,
  getTotalConnectedUsers,
  useAppSelector,
  useOverridableFeatureFlags,
} from '@lib/store';
import {
  getOfficeWorkdaysForDate,
  getWorkdaysForDate,
  getWorkdaysWithWorkspaceReservationsForDate,
} from '@lib/store/src/data/timeslots/selectors';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {WorkdayTile} from '../WorkdayTile';
import {WorkdayMeetingsTile} from '../WorkdayTile/WorkdayMeetingsTile';
import {WorkdayConnectionsTile} from '../WorkdayTile/WorkdayConnectionsTile';
import {WorkspaceInvitationTile} from '../CalendarDayOverview/components/tiles/WorkspaceInvitationTile';
import {NoConnections} from '../../home/Connections/EmptyState';
import {WorkdayMeetingsEmptyTile} from '../WorkdayTile/WorkdayMeetings';
import {DaySummaryLoader} from './DaySummaryLoader';
import {dateToLocalDateString} from '@lib/store/src/data/timeslots/dateTimeHelpers';

interface DaySummaryProps {
  selectedDate: string;
  isLoading?: boolean;
}

export const DaySummary = ({selectedDate, isLoading}: DaySummaryProps) => {
  const features = useOverridableFeatureFlags();
  const {t} = useTranslation();
  const date = useMemoDate(selectedDate);
  const dateKey = dateToLocalDateString(date);

  // Workdays
  const workdays = useAppSelector(getWorkdaysForDate(dateKey));
  const officeWorkdays = useAppSelector(getOfficeWorkdaysForDate(dateKey));

  // Prerequisites for loading meetings
  const meetingsOutsideOfWorkdays = useAppSelector((state) => getMeetingsOutsideOfWorkdays(state, dateKey));
  const isMeetingsOutsideOfWorkdaysTileVisible = !!workdays?.data?.length && meetingsOutsideOfWorkdays.length > 0;
  const isCalendarConsentInvalid = useAppSelector(getIsUserCalendarConsentInvalid);
  const isMeetingsCalendarActive = useAppSelector(getIsMeetingsCalendarActive);

  // Invites
  const reservations = useAppSelector(getWorkdaysWithWorkspaceReservationsForDate(dateKey));
  const pendingWorkspaceInvitations = reservations?.unparented.filter(
    (u) => u.invitationTo && u.status === 'InvitationPending',
  );

  const connectionsTotal = useAppSelector(getTotalConnectedUsers);

  if (isLoading) {
    return <DaySummaryLoader />;
  }

  return (
    <div className="flex flex-col gap-5 pb-8 relative">
      {pendingWorkspaceInvitations && pendingWorkspaceInvitations.length > 0 && (
        <div className="flex flex-col gap-5">
          {pendingWorkspaceInvitations.map((invitation) => (
            <WorkspaceInvitationTile
              key={invitation.id}
              workspaceReservation={invitation}
            />
          ))}
        </div>
      )}

      <div className="space-y-4">
        {workdays?.data?.length ? (
          workdays.data.map((workday) => (
            <WorkdayTile
              key={workday.id}
              workday={workday}
              selectedDate={dateKey}
            />
          ))
        ) : (
          <WorkdayTile
            workday={undefined}
            selectedDate={dateKey}
          />
        )}
      </div>

      {/* When no office workday is booked, we still want to show connections and meetings for that date */}
      {!officeWorkdays?.length && (
        <>
          {isMeetingsOutsideOfWorkdaysTileVisible && features.TimeslotsUI && (
            <WorkdayMeetingsTile selectedDate={dateKey} />
          )}

          <WorkdayConnectionsTile selectedDate={dateKey} />
        </>
      )}

      {/* Empty states */}
      {connectionsTotal === 0 && <NoConnections />}
      {!isMeetingsCalendarActive && <WorkdayMeetingsEmptyTile />}
      {isCalendarConsentInvalid && (
        <WorkdayMeetingsEmptyTile
          title={t('meeting:LostCalendarConnectionBannerTitle')}
          message={t('meeting:LostCalendarConnectionBannerMessage')}
          buttonText={t('meeting:LostCalendarConnectionBannerButtonSecondary')}
          illustration="lostCalendarConnection"
        />
      )}
    </div>
  );
};
